/**
 * SuperTokens Configuration
 *
 * Centralized configuration for SuperTokens initialization and settings.
 * Handles environment variable validation and provides default values.
 *
 * @module config/supertokens
 */

import SuperTokens from 'supertokens-web-js';
import Session from 'supertokens-web-js/recipe/session';
import EmailPassword from 'supertokens-web-js/recipe/emailpassword';
import { getLoggerWithPrefix } from '@/services/shared/LoggerFactory';

/**
 * SuperTokens configuration interface
 */
export interface SuperTokensConfig {
  apiDomain: string;
  apiBasePath: string;
  appName: string;
  enableDebugLogs: boolean;
}

/**
 * Environment variable validation
 */
interface RequiredEnvVars {
  VITE_SUPERTOKENS_API_DOMAIN: string;
  VITE_APP_NAME: string;
}

/**
 * Validate required environment variables
 */
function validateEnvironmentVariables(): RequiredEnvVars {
  const requiredVars = {
    VITE_SUPERTOKENS_API_DOMAIN: import.meta.env.VITE_SUPERTOKENS_API_DOMAIN,
    VITE_APP_NAME: import.meta.env.VITE_APP_NAME,
  };
  const logger = getLoggerWithPrefix('SuperTokens');

  const missingVars = Object.entries(requiredVars)
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missingVars.length > 0) {
    const errorMessage = `Missing required environment variables: ${missingVars.join(', ')}`;
    logger.error(errorMessage);

    if (import.meta.env.PROD) {
      throw new Error(errorMessage);
    }

    // In development, provide warnings but continue with defaults
    logger.warn('Using default values for missing environment variables in development mode');
  }

  return requiredVars;
}

/**
 * Get SuperTokens configuration from environment variables
 */
export function getSupertokensConfig(): SuperTokensConfig {
  const envVars = validateEnvironmentVariables();

  // Check if we're in unified hosting mode (same origin)
  const isUnifiedHosting = import.meta.env.VITE_UNIFIED_HOSTING === 'true';

  // For unified hosting, use relative paths/same origin
  const apiDomain = isUnifiedHosting
    ? window.location.origin
    : (envVars.VITE_SUPERTOKENS_API_DOMAIN || 'http://localhost:8000');

  return {
    apiDomain,
    apiBasePath: import.meta.env.VITE_SUPERTOKENS_API_BASE_PATH || '/auth',
    appName: envVars.VITE_APP_NAME || 'BiteWise',
    enableDebugLogs: false,
  };
}

/**
 * Initialize SuperTokens with configuration
 */
export function initializeSuperTokens(): void {
  const config = getSupertokensConfig();
  const logger = getLoggerWithPrefix('SuperTokens');

  SuperTokens.init({
    clientType: 'public',
    enableDebugLogs: config.enableDebugLogs,
    appInfo: {
      apiDomain: config.apiDomain,
      apiBasePath: config.apiBasePath,
      appName: config.appName,
    },
    recipeList: [
      Session.init({
        tokenTransferMethod: 'cookie',
        onHandleEvent: (context) => {
          if (context.action === 'UNAUTHORISED') {
            logger.log('SuperTokens: Session unauthorized - user will be signed out');
          } else if (context.action === 'SIGN_OUT') {
            logger.log('SuperTokens: User signed out');
          } else if (context.action === 'REFRESH_SESSION') {
            logger.log('SuperTokens: Session refreshed');
          } else if (context.action === 'ACCESS_TOKEN_PAYLOAD_UPDATED') {
            logger.log('SuperTokens: Access token payload updated');
          }
        },
      }),
      EmailPassword.init(),
    ],
  });

  logger.debug('SuperTokens initialized successfully', {
    apiDomain: config.apiDomain,
    apiBasePath: config.apiBasePath,
    appName: config.appName,
    debugLogs: config.enableDebugLogs,
  });
}